import { useState } from 'react'
import { FileText, Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { formatDistanceToNow } from 'date-fns'
import { th } from 'date-fns/locale'

import { Button } from '@/common/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/common/ui/sheet'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { Textarea } from '@/common/ui/textarea'
import { ScrollArea } from '@/common/ui/scroll-area'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/common/ui/alert-dialog'

import {
	useQueryChatNotes,
	useMutationCreateChatNote,
	useMutationUpdateChatNote,
	useMutationDeleteChatNote,
	useQueryChatRoom,
} from '../../../api/hooks'
import { PlayerStatsPanel } from './PlayerStatsPanel'

import type { components } from '@/schema'
import { PlayerCumulativePanel } from './PlayerCumulativePanel'

type ChatNoteResponse = components['schemas']['ChatNoteResponse']

const noteFormSchema = z.object({
	title: z.string().min(1, 'กรุณากรอกหัวข้อ'),
	content: z.string().min(1, 'กรุณากรอกเนื้อหา'),
})

type NoteFormValues = z.infer<typeof noteFormSchema>

interface ChatNotesMobileSheetProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	roomId: string
}

export function ChatNotesMobileSheet({
	open,
	onOpenChange,
	roomId,
}: ChatNotesMobileSheetProps) {
	const [mode, setMode] = useState<'list' | 'create' | 'edit'>('list')
	const [editingNote, setEditingNote] = useState<ChatNoteResponse | null>(null)
	const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null)

	const { data: notesData, isLoading } = useQueryChatNotes(roomId, open)
	const { data: room } = useQueryChatRoom(roomId)
	const createNoteMutation = useMutationCreateChatNote(roomId)
	const updateNoteMutation = useMutationUpdateChatNote(roomId)
	const deleteNoteMutation = useMutationDeleteChatNote(roomId)

	const form = useForm<NoteFormValues>({
		resolver: zodResolver(noteFormSchema),
		defaultValues: {
			title: '',
			content: '',
		},
	})

	const handleCreateNote = () => {
		form.reset({ title: '', content: '' })
		setMode('create')
	}

	const handleEditNote = (note: ChatNoteResponse) => {
		setEditingNote(note)
		form.reset({ title: note.title, content: note.content })
		setMode('edit')
	}

	const handleDeleteNote = (noteId: string) => {
		setDeleteNoteId(noteId)
	}

	const confirmDelete = async () => {
		if (deleteNoteId) {
			await deleteNoteMutation.mutateAsync(deleteNoteId)
			setDeleteNoteId(null)
		}
	}

	const handleSubmit = async (values: NoteFormValues) => {
		if (mode === 'create') {
			await createNoteMutation.mutateAsync(values)
		} else if (mode === 'edit' && editingNote) {
			await updateNoteMutation.mutateAsync({
				noteId: editingNote.id,
				data: values,
			})
		}
		setMode('list')
		setEditingNote(null)
		form.reset()
	}

	const handleCancel = () => {
		setMode('list')
		setEditingNote(null)
		form.reset()
	}

	const notes = notesData?.items || []

	return (
		<>
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent side="right" className="w-full p-0 sm:max-w-md">
					<SheetHeader className="border-b px-6 py-4">
						<SheetTitle className="flex items-center gap-2">
							<FileText className="h-5 w-5" />
							{mode === 'list' && 'รายละเอียด'}
							{mode === 'create' && 'สร้างโน้ตใหม่'}
							{mode === 'edit' && 'แก้ไขโน้ต'}
						</SheetTitle>
					</SheetHeader>

					<div className="flex h-[calc(100%-65px)] flex-col">
						{mode === 'list' ? (
							<ScrollArea className="h-full">
								<div className="flex flex-col">
									{/* Player Stats Section */}
									<div className="border-b px-4 py-2">
										<PlayerStatsPanel
											roomId={roomId}
											hasPlayer={!!room?.player}
										/>
									</div>

									{/* Player Cumulative Section */}
									{room?.player?.id && (
										<div className="border-b px-4 py-2">
											<PlayerCumulativePanel playerId={room?.player?.id} />
										</div>
									)}

									{/* Notes Section */}
									<div className="flex flex-col p-4">
										<Button
											onClick={handleCreateNote}
											className="mb-4 w-full"
											variant="outline"
										>
											<Plus className="mr-2 h-4 w-4" />
											สร้างโน้ตใหม่
										</Button>

										{isLoading ? (
											<div className="flex items-center justify-center py-8">
												<Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
											</div>
										) : notes.length === 0 ? (
											<div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
												<FileText className="mb-2 h-12 w-12 opacity-50" />
												<p>ยังไม่มีโน้ต</p>
												<p className="text-sm">
													คลิกปุ่มด้านบนเพื่อสร้างโน้ตใหม่
												</p>
											</div>
										) : (
											<div className="space-y-3">
												{notes.map((note) => (
													<div
														key={note.id}
														className="group w-full rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50"
													>
														<div className="mb-2 flex items-start justify-between">
															<h4 className="font-semibold leading-tight">
																{note.title}
															</h4>
															<div className="flex gap-1">
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-7 w-7"
																	onClick={() => handleEditNote(note)}
																>
																	<Pencil className="h-3.5 w-3.5" />
																</Button>
																<Button
																	variant="ghost"
																	size="icon"
																	className="h-7 w-7 text-destructive hover:text-destructive"
																	onClick={() => handleDeleteNote(note.id)}
																>
																	<Trash2 className="h-3.5 w-3.5" />
																</Button>
															</div>
														</div>
														<p className="mb-3 whitespace-pre-wrap text-sm text-muted-foreground">
															{note.content}
														</p>
														<div className="flex items-center justify-between text-xs text-muted-foreground">
															<span>
																อัปเดตโดย{' '}
																{note.lastUpdatedBy?.username || 'ไม่ทราบ'}
															</span>
															<span>
																{formatDistanceToNow(new Date(note.updatedAt), {
																	addSuffix: true,
																	locale: th,
																})}
															</span>
														</div>
													</div>
												))}
											</div>
										)}
									</div>
								</div>
							</ScrollArea>
						) : (
							<div className="p-4">
								<Form {...form}>
									<form
										onSubmit={form.handleSubmit(handleSubmit)}
										className="space-y-4"
									>
										<FormField
											control={form.control}
											name="title"
											render={({ field }) => (
												<FormItem>
													<FormLabel>หัวข้อ</FormLabel>
													<FormControl>
														<Input placeholder="หัวข้อโน้ต" {...field} />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="content"
											render={({ field }) => (
												<FormItem>
													<FormLabel>เนื้อหา</FormLabel>
													<FormControl>
														<Textarea
															placeholder="เนื้อหาโน้ต..."
															className="min-h-[150px] resize-none"
															{...field}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<div className="flex justify-end gap-2">
											<Button
												type="button"
												variant="outline"
												onClick={handleCancel}
											>
												ยกเลิก
											</Button>
											<Button
												type="submit"
												disabled={
													createNoteMutation.isPending ||
													updateNoteMutation.isPending
												}
											>
												{(createNoteMutation.isPending ||
													updateNoteMutation.isPending) && (
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
												)}
												{mode === 'create' ? 'สร้าง' : 'บันทึก'}
											</Button>
										</div>
									</form>
								</Form>
							</div>
						)}
					</div>
				</SheetContent>
			</Sheet>

			<AlertDialog
				open={!!deleteNoteId}
				onOpenChange={(open) => !open && setDeleteNoteId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>ยืนยันการลบโน้ต</AlertDialogTitle>
						<AlertDialogDescription>
							คุณแน่ใจหรือไม่ว่าต้องการลบโน้ตนี้?
							การดำเนินการนี้ไม่สามารถย้อนกลับได้
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>ยกเลิก</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
						>
							{deleteNoteMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							ลบ
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
